import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HeroImage from "../../../components/Hero/HeroImage";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getReglementsArbitrage } from "../../../services/reglementarbitrageServices";
import ReglementCard from "./ReglementCard";
import { FaBookReader, FaLink, FaImage } from "react-icons/fa";

const sortReglements = (items = []) => {
  return [...items].sort((a, b) => {
    const orderA = Number(a?.display_order ?? 0);
    const orderB = Number(b?.display_order ?? 0);
    if (orderA !== orderB) return orderA - orderB;
    return Number(a?.id ?? 0) - Number(b?.id ?? 0);
  });
};

function StatChip({ icon, label, value, accent = "indigo" }) {
  const accents = {
    indigo:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    emerald:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    amber:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300",
  };

  return (
    <div className={`rounded-2xl border px-4 py-3 ${accents[accent]}`}>
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-900/50">
          {icon}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] opacity-75">
            {label}
          </div>
          <div className="text-xl font-bold text-slate-900 dark:text-white">
            {value}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ReglementArbitragePage() {
  useScrollToTop();
  const navigate = useNavigate();

  const [reglements, setReglements] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await getReglementsArbitrage();
      setReglements(Array.isArray(data) ? sortReglements(data) : []);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des règlements d'arbitrage :",
        error,
      );
      setReglements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const stats = useMemo(() => {
    const total = reglements.length;
    const withLinks = reglements.filter((r) => !!r?.lien_url).length;
    const withImages = reglements.filter((r) => !!r?.lien_url_image).length;

    return { total, withLinks, withImages };
  }, [reglements]);

  return (
    <div className="w-full pt-[96px] font-satoshi">
      <HeroImage
        title="Règlements d’arbitrage"
        subtitle="Consultez les règlements et documents utiles destinés aux arbitres et commissaires sportifs."
        imageUrl="/images/illustrations/illustration_home_004.webp"
      >
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-white/90">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="transition hover:underline"
          >
            Accueil
          </button>
          <span>/</span>
          <span>Arbitrage</span>
          <span>/</span>
          <span className="font-semibold text-white">Règlements</span>
        </div>
      </HeroImage>

      <div className="mx-auto max-w-[1280px] px-4 pb-10 pt-6">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

          <div className="p-4 sm:p-5 lg:p-6">
            <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                  Arbitrage
                </p>
                <h2 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
                  Documents et règlements disponibles
                </h2>
                <p className="mt-2 max-w-3xl text-sm text-slate-600 dark:text-slate-300">
                  Retrouvez ici l’ensemble des règlements d’arbitrage publiés
                  par la commission Arbitrage.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[720px]">
                <StatChip
                  icon={<FaBookReader />}
                  label="Règlements"
                  value={stats.total}
                  accent="indigo"
                />
                <StatChip
                  icon={<FaLink />}
                  label="Avec lien"
                  value={stats.withLinks}
                  accent="emerald"
                />
                <StatChip
                  icon={<FaImage />}
                  label="Avec visuel"
                  value={stats.withImages}
                  accent="amber"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <span className="rounded-full bg-slate-100 px-3 py-1 dark:bg-slate-800">
            {reglements.length} règlement{reglements.length > 1 ? "s" : ""}
          </span>

          {!loading && reglements.length > 0 && (
            <span className="rounded-full bg-indigo-50 px-3 py-1 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
              Trié par ordre d’affichage
            </span>
          )}
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="rounded-3xl border border-slate-200 bg-white px-4 py-12 text-center text-slate-500 shadow-sm dark:border-slate-800 dark:bg-slate-900">
              Chargement des règlements…
            </div>
          ) : reglements.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                <FaBookReader className="text-2xl" />
              </div>

              <h3 className="mt-4 text-xl font-bold text-slate-900 dark:text-white">
                Aucun règlement disponible
              </h3>

              <p className="mx-auto mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                Aucun règlement d’arbitrage n’a encore été publié.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {reglements.map((reglement) => (
                <ReglementCard key={reglement.id} reglement={reglement} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
